/**
 * Sync page-level meta tags during SPA navigation. Each Solid page calls
 * `setPageMeta({ title, description?, ogImage? })` from a createEffect so
 * `document.title` and the og:* tags stay in sync with the displayed
 * content. The RootLayout fires Google Analytics page_view events when
 * the URL changes.
 */

interface PageMeta {
    /** Page-specific prefix. Joined with the site title via " - ". */
    title: string | null;
    /** Pass true to use `title` as-is, without appending the site title. */
    raw?: boolean;
    description?: string;
    ogImage?: string;
}

function siteTitle(): string {
    return window.__BOOTSTRAP__?.site?.title ?? '';
}

export function buildPageTitle(prefix: string | null, raw = false): string {
    const site = siteTitle();
    if (raw && prefix) return prefix;
    if (!prefix) return site;
    if (!site) return prefix;
    return `${prefix} - ${site}`;
}

const upsertMeta = (selector: string, attr: 'name' | 'property', key: string, content: string): void => {
    let el = document.head.querySelector<HTMLMetaElement>(selector);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
};

export function setPageMeta(meta: PageMeta): void {
    if (typeof document === 'undefined') return;
    const fullTitle = buildPageTitle(meta.title, meta.raw === true);
    document.title = fullTitle;
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    if (meta.description) {
        upsertMeta('meta[name="description"]', 'name', 'description', meta.description);
        upsertMeta('meta[property="og:description"]', 'property', 'og:description', meta.description);
    }
    if (meta.ogImage) {
        upsertMeta('meta[property="og:image"]', 'property', 'og:image', meta.ogImage);
    }
}

declare global {
    interface Window {
        gtag?: (...args: unknown[]) => void;
    }
}

/** Fire a GA4 page_view for SPA navigation. */
export function trackPageView(pathname: string, search: string): void {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
        page_path: pathname + (search || ''),
        page_title: document.title,
        page_location: window.location.href,
    });
}
